import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, Observable, of, tap } from "rxjs";
import { environment } from "@env/environment";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Broker, BrokerRequest } from "./broker.interface";

@Injectable({ providedIn: 'root' })
export class BrokerService {

    private baseUrl = environment.administration_url + '/admin/broker-management';

    private _broker: BehaviorSubject<Broker | null> = new BehaviorSubject<Broker | null>(null);
    private _brokers: BehaviorSubject<Broker[]> = new BehaviorSubject<Broker[]>([]);
    private _metadata: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    get broker$(): Observable<Broker | null> {
        return this._broker.asObservable();
    }

    get brokers$(): Observable<Broker[]> {
        return this._brokers.asObservable();
    }

    get metadata$(): Observable<any> {
        return this._metadata.asObservable();
    }

    constructor(private _httpClient: HttpClient) {}

    getAll(filters?: { name?: string; email?: string; page?: number; size?: number; sort?: string }): Observable<any> {
        let params = new HttpParams();

        if (filters) {
            if (filters.name) params = params.set('name', filters.name);
            if (filters.email) params = params.set('email', filters.email);
            if (filters.page !== undefined) params = params.set('page', filters.page.toString());
            if (filters.size !== undefined) params = params.set('size', filters.size.toString());
            if (filters.sort) params = params.set('sort', filters.sort);
        }

        return this._httpClient.get<any>(`${this.baseUrl}`, { params })
            .pipe(
                tap((response: any) => {
                    this._brokers.next(response?.content || []);
                    this._metadata.next(response);
                }),
                catchError(() => of({ content: [] }))
            );
    }

    getAllBrokers(): Observable<Broker[]> {
        return this._httpClient.get<Broker[]>(`${this.baseUrl}/all`)
            .pipe(
                tap((brokers: Broker[]) => {
                    this._brokers.next(brokers || []);
                }),
                catchError(() => of([]))
            );
    }

    getById(uuid: string): Observable<Broker> {
        return this._httpClient.get<Broker>(`${this.baseUrl}/${uuid}`)
            .pipe(
                tap((broker: Broker) => {
                    this._broker.next(broker);
                }),
                catchError(() => of({} as Broker))
            );
    }

    create(broker: BrokerRequest): Observable<Broker> {
        const payload = {
            ...broker,
            typePointOfSale: 'BROKER'
        };

        return this._httpClient.post<Broker>(`${this.baseUrl}`, payload)
            .pipe(
                tap((newBroker: Broker) => {
                    this._broker.next(newBroker);
                }),
                catchError(() => of({} as Broker))
            );
    }

    createWithUser(data: { managementEntity: any; user: any }): Observable<Broker> {
        const payload = {
            managementEntity: {
                ...data.managementEntity,
                typePointOfSale: 'BROKER'
            },
            user: data.user
        };

        return this._httpClient.post<Broker>(`${this.baseUrl}-and-users`, payload)
            .pipe(
                tap((newBroker: Broker) => {
                    this._broker.next(newBroker);
                }),
                catchError(() => of({} as Broker))
            );
    }

    update(uuid: string, broker: BrokerRequest): Observable<Broker> {
        const payload = {
            ...broker,
            typePointOfSale: 'BROKER'
        };

        return this._httpClient.put<Broker>(`${this.baseUrl}/${uuid}`, payload)
            .pipe(
                tap((updatedBroker: Broker) => {
                    this._broker.next(updatedBroker);
                }),
                catchError(() => of({} as Broker))
            );
    }

    delete(uuid: string): Observable<void> {
        return this._httpClient.delete<void>(`${this.baseUrl}/${uuid}`)
            .pipe(
                catchError(() => of(undefined))
            );
    }

    uploadLogo(uuid: string, file: File): Observable<Broker> {
        const formData = new FormData();
        formData.append('file', file);

        return this._httpClient.post<Broker>(`${this.baseUrl}/${uuid}/logo`, formData)
            .pipe(
                tap((broker: Broker) => {
                    this._broker.next(broker);
                }),
                catchError(() => of({} as Broker))
            );
    }

    deleteLogo(uuid: string): Observable<Broker> {
        return this._httpClient.delete<Broker>(`${this.baseUrl}/${uuid}/logo`)
            .pipe(
                tap((broker: Broker) => {
                    this._broker.next(broker);
                }),
                catchError(() => of({} as Broker))
            );
    }
}
